import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
    Checkbox,
    Grid,
    IconButton,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import { Body, GreyBody, OnwardPagination, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import {
    canAddServiceLine,
    getServiceCatalogServices,
    serviceOptionLabel,
} from '@/components/ShipmentForm/utilities/serviceCatalog';
import ServiceTimeInput from '@/components/ServiceTimeInput';
import { formatHHMM } from '@/utilities/hhmm';
import { SERVICE_MUTATION_FAILED, SERVICE_SPLIT_PRICING_FAILED, useOrderServices } from './hooks';

const ROWS_PER_PAGE = 15;

// The hook throws these only after it has already toasted the failure, so a caller that sees one
// should stay quiet rather than stack a second message on top.
const alreadyNotified = (e) => e?.message === SERVICE_MUTATION_FAILED || e?.message === SERVICE_SPLIT_PRICING_FAILED;

// Row-level edits are fired without awaiting, so their rejection needs somewhere to land — the
// user has already been notified by the time it gets here.
const ignoreHandled = (promise) => promise?.catch?.(() => {});

// Editable per-line service time (HH:MM, stored as minutes). Read-only text when
// the user can't edit; the editable case delegates to the shared ServiceTimeInput.
const ServiceTimeCell = ({ svc, disabled, onSave }) => {
    const stored = Number.isFinite(svc.service_time) ? svc.service_time : null;

    if (disabled) {
        return stored == null ? '--' : formatHHMM(stored);
    }

    return (
        <ServiceTimeInput
            value={svc.service_time}
            onCommit={(next) => onSave(svc.service_id, next)}
            ariaLabel={`${svc.code} service time`}
        />
    );
};

// Order details "Services" tab: a grid of the order's service lines with a non-billable toggle, plus
// (for carrier admins) Add Service and Split Services. Split moves the checked lines onto a new
// service order (clone) and removes them here. Read-only for non-carrier-admins.
const OrderServicesTab = ({ order, serviceCatalog, isCarrierAdmin, onNotify }) => {
    const { services, adding, splitting, addService, setBillable, setServiceTime, removeService, splitServices } =
        useOrderServices(order, { onNotify });

    const [selected, setSelected] = useState(() => new Set());
    const [addOpen, setAddOpen] = useState(false);
    const [addSelected, setAddSelected] = useState(null);
    const [addQty, setAddQty] = useState(1);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [splitOpen, setSplitOpen] = useState(false);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [page, setPage] = useState(0);

    const catalogOptions = useMemo(() => getServiceCatalogServices(serviceCatalog), [serviceCatalog]);

    const pageCount = Math.max(1, Math.ceil(services.length / ROWS_PER_PAGE));
    const safePage = Math.min(page, pageCount - 1);
    const pagedServices = useMemo(
        () => services.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE),
        [services, safePage]
    );

    const toggle = (id) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const closeAdd = () => {
        setAddOpen(false);
        setAddSelected(null);
        setAddQty(1);
    };

    const handleAdd = () => {
        if (!canAddServiceLine(addSelected, addQty) || addSubmitting) return;
        setAddSubmitting(true);
        addService(addSelected, addQty)
            .then(closeAdd)
            .catch((e) => {
                if (alreadyNotified(e)) return;
                onNotify?.({
                    severity: 'error',
                    message: 'Something went wrong adding the service. Please try again.',
                });
            })
            .finally(() => setAddSubmitting(false));
    };

    const closeSplit = () => {
        setSplitOpen(false);
        setNotes('');
    };

    const handleSplit = () => {
        if (submitting) return;
        setSubmitting(true);
        splitServices([...selected], notes.trim() || null)
            .then(() => {
                closeSplit();
                setSelected(new Set());
            })
            .catch((e) => {
                // Pricing and mutation failures were already notified inside the hook — don't
                // double-toast those.
                if (alreadyNotified(e)) return;
                onNotify?.({
                    severity: 'error',
                    message: 'Something went wrong creating the service order. Please try again.',
                });
            })
            .finally(() => setSubmitting(false));
    };

    const colSpan = isCarrierAdmin ? 7 : 5;

    return (
        <div
            css={css`
                width: 100%;
            `}
        >
            {isCarrierAdmin ? (
                <Grid
                    container
                    justifyContent="flex-end"
                    css={css`
                        margin-bottom: 1rem;
                    `}
                >
                    <PrimaryButton onClick={() => setAddOpen(true)} disabled={adding}>
                        Add Service
                    </PrimaryButton>
                </Grid>
            ) : null}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        {isCarrierAdmin ? <TableCell padding="checkbox" /> : null}
                        <TableCell>Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Qty</TableCell>
                        <TableCell align="center">Billable</TableCell>
                        <TableCell align="center">Service Time</TableCell>
                        {isCarrierAdmin ? <TableCell align="right">Actions</TableCell> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {services.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={colSpan}>
                                <GreyBody>No services on this order yet.</GreyBody>
                            </TableCell>
                        </TableRow>
                    ) : (
                        pagedServices.map((svc) => (
                            <TableRow key={svc.service_id}>
                                {isCarrierAdmin ? (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selected.has(svc.service_id)}
                                            onChange={() => toggle(svc.service_id)}
                                            aria-label={`Select ${svc.code}`}
                                        />
                                    </TableCell>
                                ) : null}
                                <TableCell>{svc.code}</TableCell>
                                <TableCell>{svc.description || '--'}</TableCell>
                                <TableCell align="center">{svc.quantity}</TableCell>
                                <TableCell align="center">
                                    <Switch
                                        size="small"
                                        color="primary"
                                        checked={svc.billable !== false}
                                        disabled={!isCarrierAdmin}
                                        onChange={(e) => ignoreHandled(setBillable(svc.service_id, e.target.checked))}
                                        inputProps={{ 'aria-label': `${svc.code} billable` }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <ServiceTimeCell
                                        svc={svc}
                                        disabled={!isCarrierAdmin}
                                        onSave={(id, next) => ignoreHandled(setServiceTime(id, next))}
                                    />
                                </TableCell>
                                {isCarrierAdmin ? (
                                    <TableCell align="right">
                                        <Tooltip title="Remove">
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${svc.code}`}
                                                onClick={() => ignoreHandled(removeService(svc.service_id))}
                                                css={css`
                                                    color: ${colors.reds.primary};
                                                `}
                                            >
                                                <DeleteOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                ) : null}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            <Grid
                container
                justifyContent="center"
                css={css`
                    padding: 1rem 0;
                `}
            >
                <OnwardPagination count={pageCount} page={safePage + 1} onChange={(_, p) => setPage(p - 1)} />
            </Grid>

            {isCarrierAdmin ? (
                <Grid
                    container
                    justifyContent="flex-end"
                    css={css`
                        margin-top: 1rem;
                    `}
                >
                    <PrimaryButton onClick={() => setSplitOpen(true)} disabled={selected.size === 0 || splitting}>
                        Split Services
                    </PrimaryButton>
                </Grid>
            ) : null}

            {/* Add Service */}
            {/* fullWidth so the dialog holds its width instead of hugging its content: the picker now
                renders "CODE - Description", which a content-sized dialog truncated. */}
            <NavResponsiveModal open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
                <ModalHeader title="Add Service" onClose={closeAdd} />
                <ModalContent>
                    <Autocomplete
                        autoHighlight
                        options={catalogOptions}
                        value={addSelected}
                        getOptionLabel={serviceOptionLabel}
                        onChange={(_, value) => setAddSelected(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Service"
                                placeholder="Search service codes or descriptions"
                            />
                        )}
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    />
                    <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        label="Quantity"
                        value={addQty}
                        inputProps={{ min: 1, step: 1 }}
                        onChange={(e) => setAddQty(e.target.value)}
                    />
                </ModalContent>
                <ModalActions>
                    <SecondaryButton onClick={closeAdd}>Cancel</SecondaryButton>
                    <PrimaryButton
                        disabled={!canAddServiceLine(addSelected, addQty) || adding || addSubmitting}
                        loading={adding || addSubmitting}
                        onClick={handleAdd}
                    >
                        Add
                    </PrimaryButton>
                </ModalActions>
            </NavResponsiveModal>

            {/* Split Services → new service order */}
            <NavResponsiveModal open={splitOpen} onClose={closeSplit}>
                <ModalHeader title="Split Services" onClose={closeSplit} />
                <ModalContent>
                    <Body
                        css={css`
                            display: block;
                            margin-bottom: 1rem;
                        `}
                    >
                        Move the {selected.size} selected service{selected.size === 1 ? '' : 's'} onto a new service
                        order linked to this one. The new order bills only those services.
                    </Body>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        variant="outlined"
                        label="Driver notes (optional)"
                        placeholder="Notes for the driver at the time of service"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </ModalContent>
                <ModalActions>
                    <SecondaryButton onClick={closeSplit}>Cancel</SecondaryButton>
                    <PrimaryButton
                        disabled={submitting || splitting}
                        loading={submitting || splitting}
                        onClick={handleSplit}
                    >
                        Create Service Order
                    </PrimaryButton>
                </ModalActions>
            </NavResponsiveModal>
        </div>
    );
};

export default OrderServicesTab;
